import React from 'react';
import { Col } from 'antd';
import { useTranslation } from 'react-i18next';
import FormItem from 'src/components/form-item';
import DoubleArrowImage from 'src/assets/img/double-arrow.svg';
import TableContent from 'src/components/table-content';
import './style.scss';
import AvatarDescription from 'src/components/avatar-description';

/**
 * ConfirmFitItem component - used in ConfirmFitModal in Deallocation and Need pages.
 * 
 * @param {Object} props - Props for the PageTitle component.
 * @param {Object} props.fitPeople - - The object containing deallocation and need data.
 * @param {string} props.type - Text indicating if it is for 'people' page or 'need' page.
 * @param {Object} props.need - If it is on Need page, get need object as property.
 * @param {Array} props.deallocationIds - The array of deallocation ids.
 * @param {Array} props.deallocations - The array of deallocation objects.
 * @returns {JSX.Element} The ConfirmFitItem component.
 * @example
 * <ConfirmFitItem
 *     fitPeople={fitPeople}
 *     type={type}
 *     deallocationIds={deallocationIds}
 *     deallocations={deallocations}
 */
function ConfirmFitItem({ fitPeople, type, deallocationIds, deallocations }) {
    const { t } = useTranslation();

    const {need, deallocation} = fitPeople
    const filteredDeallocations = deallocationIds.length > 0 ? deallocations.filter(deallocation => deallocationIds.includes(deallocation.id)) : [deallocation]
    
    /**
     * Generates needItem container
     * @returns {JSX.Element} NeedItem
     */
    const needItem = () => (
        <FormItem label={t('needsTitle')} aria-label='opportunity'>
            <Col aria-label='opportunity'>
                <div className='confirm-item-container' aria-describedby='opportunity' tabIndex={0} >
                    <TableContent 
                        mainContent={`${need.title} (${need.requisitionCode})`}
                        subContent={`${t('client')}: ${need.client ?? '-'}`}
                    />
                </div>
            </Col>
        </FormItem>
    )

    /**
     * Generates peopleItem container
     * @returns {JSX.Element}
     */
    const peopleItem = () => filteredDeallocations.map(dealloc => (
        <Col aria-label='profile' key={dealloc.id}>
            <FormItem label={t('profile')}>
                <div className='confirm-item-container' aria-describedby='profile' tabIndex={0}>
                    <AvatarDescription
                        mainText={dealloc.collaborator_deallocation.login}
                        mainTextAriaLabel={dealloc.collaborator_deallocation.login}
                        mainTextTranslation={t('name')}
                        subText={[
                                {
                                    description: t('role'),
                                    value: dealloc.collaborator_deallocation.role,
                                },
                                {
                                    description: t('level'),
                                    value: dealloc.collaborator_deallocation.positionMap,
                                },
                            ]}
                    />
                </div>
            </FormItem>
        </Col>
    ))

    return (
        type === 'people' ? 
            (
                <div className='confirm-item--flex'>
                {peopleItem()}
                <img src={DoubleArrowImage} alt='match arrow' className='arrow-image' />
                {needItem()}
            </div>
            ) :
            (
                <div className='confirm-item--flex'>
                {needItem()}
                <img src={DoubleArrowImage} alt='match arrow' className='arrow-image' />
                <div className='confirm-item--align'>
                {peopleItem()}
                </div>
                </div>
            )
    );
}

ConfirmFitItem.defaultProps = {
    fitPeople: {},
    type: '', 
    deallocations: {},
    deallocationIds: []
};

export default ConfirmFitItem;
